import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Wine, ShoppingBasket } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PlaceThumbnail } from "@/components/explore/PlaceThumbnail";
import type { PlaceOutingSignal } from "@/hooks/usePlaceOutingSignals";
import { humanizeEventDate } from "@/lib/utflykt/humanizeEventDate";

interface UtflyktCardProps {
  signal: PlaceOutingSignal;
  coverUrl?: string | null;
}

/**
 * Card for "Förslag nära dig" section.
 * Compact with small cover image, title-first hierarchy, max 2 chips.
 */
export function UtflyktCard({ signal, coverUrl }: UtflyktCardProps) {
  const navigate = useNavigate();
  const eventLabel = humanizeEventDate(signal.next_event_at);

  // Build chips (max 2 total)
  const chips: { Icon?: LucideIcon; label: string }[] = [];

  if (eventLabel) {
    chips.push({ Icon: Calendar, label: eventLabel });
  }
  if (chips.length < 2 && signal.has_farm_sale_alcohol) {
    chips.push({ Icon: Wine, label: "Dryck" });
  }
  if (chips.length < 2 && signal.has_farm_shop) {
    chips.push({ Icon: ShoppingBasket, label: "Handla från gården" });
  }

  const subtitle = [signal.municipality, signal.landskap]
    .filter(Boolean)
    .join(", ");

  const href = `/place/${signal.slug}`;

  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full text-left active:bg-muted/30 transition-colors cursor-pointer rounded-lg border border-border/50 overflow-hidden hover:border-border"
      onClick={() => navigate(href)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") navigate(href);
      }}
    >
      {/* Cover image – 16:9 */}
      <div className="w-full aspect-video bg-muted relative">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={signal.name}
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MapPin className="h-6 w-6 text-muted-foreground/40" />
          </div>
        )}
      </div>

      {/* Text content */}
      <div className="p-3 space-y-1.5">
        {/* Name */}
        <h3 className="text-sm font-semibold text-foreground line-clamp-1">
          {signal.name}
        </h3>

        {/* Location */}
        {subtitle && (
          <div className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin aria-hidden className="h-3 w-3 shrink-0" />
            <span className="line-clamp-1">{subtitle}</span>
          </div>
        )}

        {/* Chips */}
        {chips.length > 0 && (
          <div className="flex flex-wrap items-center gap-1">
            {chips.map((chip, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="text-[11px] font-normal px-2 py-0.5"
              >
                {chip.Icon && <chip.Icon aria-hidden className="h-3 w-3 shrink-0 mr-0.5" />}
                {chip.label}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
